import { useState, useEffect, forwardRef, useImperativeHandle, ForwardRefRenderFunction } from 'react';
import { Card, CardContent, Typography, Box, List, ListItem, ListItemText, CircularProgress, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getRecentlyUpdatedEligibilityData, EligibilityDataResponse } from '../../../services/eligibilityService';
import { formatDateForDisplay } from '../../../utils/dateUtils';
import PersonIcon from '@mui/icons-material/Person';
import { ListItemIcon } from '@mui/material';

// For TypeScript support
export type RecentEligibilityUpdatesCardRef = {
  fetchData: () => Promise<void>;
};

interface RecentEligibilityItem {
  id: number;
  patientName: string;
  lastUpdated: string;
  isNew: boolean;
  createdAt: string;
}

const RecentEligibilityUpdatesCardComponent: ForwardRefRenderFunction<RecentEligibilityUpdatesCardRef> = (_, ref) => {
  const [loading, setLoading] = useState(true);
  const [eligibilityData, setEligibilityData] = useState<RecentEligibilityItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();

  useImperativeHandle(ref, () => ({
    fetchData
  }));

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Get recent updates with a reasonable limit for counting and display
      // We'll get more than we need for display to get an accurate count
      const recentData = await getRecentlyUpdatedEligibilityData(7, 20);
      
      // Set total count from the fetched data
      setTotalCount(recentData.length);
      
      // Take only the first 3 items for display
      const limitedData = recentData.slice(0, 3);
      
      const processedData: RecentEligibilityItem[] = limitedData.map((item: EligibilityDataResponse) => {
        const patientName = `${item.firstName} ${item.lastName}`.trim();
        const lastUpdated = item.updatedAt || item.createdAt;
        
        // Consider it "new" if the createdDate and updatedDate are equal
        const createdDate = new Date(item.createdAt);
        const updatedDate = item.updatedAt ? new Date(item.updatedAt) : null;
        const isNew = !updatedDate || createdDate.getTime() === updatedDate.getTime();
        
        return {
          id: item.id,
          patientName,
          lastUpdated,
          isNew,
          createdAt: item.createdAt
        };
      });
      
      setEligibilityData(processedData);
    } catch (error) {
      console.error('Failed to fetch recent eligibility updates:', error);
      setEligibilityData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => {
    // Navigate to eligibility list with a filter for recent updates
    navigate('/eligibility');
  };

  const handleItemClick = (id: number) => {
    navigate(`/eligibility/${id}`);
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Recent Eligibility Updates
        </Typography>
        
        <Box sx={{ height: 248, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : eligibilityData.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 2 }}>
              No recent eligibility updates found
            </Typography>
          ) : (
            <List sx={{ width: '100%' }}>
              {eligibilityData.map((item, index) => (
                <ListItem 
                  key={item.id} 
                  divider={index < eligibilityData.length - 1}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover'
                    }
                  }}
                  onClick={() => handleItemClick(item.id)}
                >
                  <ListItemIcon>
                    <PersonIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography noWrap>
                          {item.patientName}
                        </Typography>
                        {item.isNew && (
                          <Chip 
                            label="New" 
                            size="small" 
                            color="success" 
                            variant="outlined"
                          />
                        )}
                        {!item.isNew && (
                          <Chip 
                            label="Updated" 
                            size="small" 
                            color="info" 
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Typography component="span" variant="body2" color="text.secondary">
                        Last updated: {formatDateForDisplay(item.lastUpdated)}
                      </Typography>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
        
        {totalCount > 4 && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="outlined"
              onClick={handleViewAll}
            >
              View All {totalCount} Eligibility Files
            </Button>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

const RecentEligibilityUpdatesCard = forwardRef(RecentEligibilityUpdatesCardComponent);
export default RecentEligibilityUpdatesCard;
